import { Phone, Mail, MapPin } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFadeIn } from "@/hooks/useFadeIn";
import SmartCaseEvaluation from "@/components/SmartCaseEvaluation";

const ContactSection = () => {
  const { t } = useLanguage();
  const { ref: imageRef, isVisible: imageVisible } = useFadeIn();
  const { ref: formRef, isVisible: formVisible } = useFadeIn({ delay: 200 });

  return (
    <section id="contact" className="scroll-mt-header">
      <div className="grid lg:grid-cols-2 min-h-[750px]">
        {/* Left Side - Full Height Image with Overlay */}
        <div
          ref={imageRef}
          className={`relative hidden lg:block fade-up ${imageVisible ? 'visible' : ''}`}
        >
          <img
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80"
            alt="Professional law office building"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/30" />
          
          {/* Contact Info Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-12">
            <div className={`fade-up delay-300 ${imageVisible ? 'visible' : ''}`}>
              <h3 className="font-heading text-3xl md:text-4xl font-bold text-white mb-8">
                {t("contact.imageTitle")}
              </h3>
              <div className="space-y-5">
                <div className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center group-hover:bg-gold/30 transition-colors">
                    <MapPin className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <p className="text-white/60 text-sm mb-1">{t("contact.info.offices")}</p>
                    <p className="text-white font-medium">Kew Gardens, NY · West New York, NJ · Boca Raton, FL</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center group-hover:bg-gold/30 transition-colors">
                    <Phone className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <p className="text-white/60 text-sm mb-1">{t("contact.info.phone")}</p>
                    <p className="text-white font-medium">(212) 586-0500</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 group">
                  <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center group-hover:bg-gold/30 transition-colors">
                    <Mail className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <p className="text-white/60 text-sm mb-1">{t("contact.info.email")}</p>
                    <a href="mailto:info@arandialawfirm.com" className="text-white font-medium hover:text-gold transition-colors">info@arandialawfirm.com</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Smart Case Evaluation */}
        <div
          ref={formRef}
          className={`bg-navy-radial py-16 lg:py-24 px-6 lg:px-16 flex items-center fade-up ${formVisible ? 'visible' : ''}`}
        >
          <div className="w-full">
            <span className="eyebrow-text mb-3 block">
              {t("contact.label")}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mt-3 mb-4">
              {t("contact.title")}
            </h2>
            <p className="text-white/60 mb-10 text-lg">
              {t("contact.subtitle")}
            </p>

            <SmartCaseEvaluation />

            {/* Mobile Contact Info */}
            <div className="lg:hidden mt-12 pt-8 border-t border-white/10 space-y-4">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-gold" />
                <span className="text-white/80">Kew Gardens, NY · West New York, NJ · Boca Raton, FL</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gold" />
                <span className="text-white/80">(212) 586-0500</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gold" />
                <a href="mailto:info@arandialawfirm.com" className="text-white/80 hover:text-gold transition-colors">info@arandialawfirm.com</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
